import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Select,
  MenuItem,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Dialog,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import { Table, TableBody, TableRow, TableCell, TableContainer, Paper } from "@mui/material";

import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";

import SuiBox from "components/SuiBox";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";

import ApoderoadosApi from "api/apoderadosApi";

const ApoderadoClienteModal = ({ open, onClose, acreedor, onSuccess }) => {
  const [error, setError] = useState(undefined);
  const [apoderados, setApoderados] = useState([]);
  const [apoderado, setApoderado] = useState(null);
  const [apoderadoExistente, setApoderadoExistente] = useState(false);
  const [listaRespClientes, setListaRespClientes] = useState([]);
  const [listaResponsables, setListaResponsables] = useState([]);
  const [tipoIdentificacion, setTipoIdentificacion] = useState("");
  const [identificacion, setIdentificacion] = useState("");
  const [nombreCompleto, setNombreCompleto] = useState("");
  const [tarjetaProfesional, setTarjetaProfesional] = useState("");
  const [celular, setCelular] = useState("");
  const [email, setEmail] = useState("");
  const [ciudad, setCiudad] = useState("");
  const [direccion, setDireccion] = useState("");

  // Trae los apoderados asociados al acreedor (lista del modal)
  const fetchInitialData = async () => {
    try {
      const [apoderadosRes] = await Promise.all([
        ApoderoadosApi.fetchApoderadosAcreedor(acreedor.id),
      ]);
      const apode = apoderadosRes?.data || [];
      if (apode && !apode.message) {
        setApoderados(apode || []);
      } else {
        setApoderados([]);
      }
    } catch (err) {
      console.error("Error al obtener los datos iniciales:", err);
    }
  };

  useEffect(() => {
    if (acreedor) {
      fetchInitialData();
    } else {
      setApoderados([]);
    }
    // reset formulario cuando cambia el acreedor seleccionado
    resetFormFields();
  }, [acreedor]);

  const handleIdentificacionChange = async (event) => {
    if (!event || !event.target) return;
    const id = event.target.value;
    if (!/^\d{0,15}$/.test(id)) {
      setError("La Identificación debe tener sólo números");
      return;
    }
    setIdentificacion(id);
    if (!id || id.length < 8) {
      // aún no buscar
      return;
    }
    try {
      const apoderadoResponse = await ApoderoadosApi.fetchApoderadoByIdentificacion(id);
      const found = apoderadoResponse?.data;
      if (found && !found.error) {
        // proteger que list_acreedores siempre sea un array
        const responsablesArr = Array.isArray(found.responsable?.list_acreedores)
          ? found.responsable.list_acreedores
          : [];
        const clientesArr = Array.isArray(found.responsable?.list_clientes)
          ? found.responsable.list_clientes
          : [];
        // Verificar si ese apoderado ya está en la lista del modal (este acreedor)
        if (apoderados.some((a) => a.identificacion === found.identificacion)) {
          setError("Este apoderado ya está asociado a este acreedor.");
          // aún guardamos el apoderado para permitir edición si se desea,
          // pero prevenimos registro duplicado. (Separar comportamiento si no quieres permitir edición)
          setApoderado(found);
          setApoderadoExistente(true);
          setListaResponsables(responsablesArr);
          setListaRespClientes(clientesArr);
          return;
        }
        setApoderado(found);
        setApoderadoExistente(true);
        setIdentificacion(found.identificacion);
        setTipoIdentificacion(found.tipo_identificacion);
        setCiudad(found.ciudad || "");
        setNombreCompleto(found.nombre_completo || "");
        setCelular(found.celular || "");
        setTarjetaProfesional(found.tarjeta_profesional || "");
        setEmail(found.email || "");
        setDireccion(found.direccion || "");
        setListaResponsables(responsablesArr);
        setListaRespClientes(clientesArr);
        setError(undefined);
      } else {
        // no existe: permitir crear nuevo
        setApoderado(null);
        setApoderadoExistente(false);
        setListaResponsables([]); // nuevo apoderado sin responsables
        setListaRespClientes([]);
        setError(undefined);
      }
    } catch (err) {
      console.error("Error al verificar los datos del apoderado", err);
      setError(obtenerMensajeError(err, "Error al verificar los datos del apoderado"));
      setApoderado(null);
      setApoderadoExistente(false);
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validateEmailInput = (value) => {
    let cleanValue = value.trim().toLowerCase();
    cleanValue = cleanValue.replace(/[^a-z0-9@._-]/g, "");
    return {
      value: cleanValue,
      isValid: isValidEmail(cleanValue),
      errorMessage: isValidEmail(cleanValue) ? "" : "Por favor ingrese un Email válido",
    };
  };

  const handleEmailChange = (e) => {
    const result = validateEmailInput(e.target.value);
    setEmail(result.value);
    setError(result.errorMessage || undefined);
  };

  const resetFormFields = () => {
    setError(undefined);
    setTipoIdentificacion("");
    setIdentificacion("");
    setNombreCompleto("");
    setTarjetaProfesional("");
    setCelular("");
    setEmail("");
    setCiudad("");
    setDireccion("");
    setApoderado(null);
    setApoderadoExistente(false);
    // NOTA: no limpiamos `apoderados` aquí (lista del modal)
    // ni limpiamos `listaResponsables` porque puede venir del apoderado cargado
    setListaRespClientes([]);
    setListaResponsables([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tipoIdentificacion) {
      setError("Debes seleccionar un Tipo de Identificación.");
      return;
    }

    // crear copia segura de la lista de acreedores (no mutar el state directamente)
    const updatedListaResponsables = Array.isArray(listaResponsables) ? [...listaResponsables] : [];

    if (!updatedListaResponsables.includes(acreedor.id)) {
      updatedListaResponsables.push(acreedor.id);
    }

    const datos = {
      identificacion: identificacion,
      tipo_identificacion: tipoIdentificacion,
      nombre_completo: nombreCompleto,
      tarjeta_profesional: tarjetaProfesional,
      celular: celular,
      email: email,
      direccion: direccion,
      ciudad: ciudad,
      responsable: {
        list_clientes: Array.isArray(listaRespClientes) ? listaRespClientes : [],
        list_acreedores: updatedListaResponsables,
      },
    };

    try {
      // En los endpoints puede venir la respuesta en .data o en la propia respuesta
      let resp;
      if (apoderado && apoderado.id) {
        resp = await ApoderoadosApi.updateApoderado(apoderado.id, datos);
      } else {
        resp = await ApoderoadosApi.createApoderado(datos);
      }

      const saved = resp?.data || resp;
      if (saved) {
        // sincronizar estado local
        setListaResponsables(updatedListaResponsables);
        setApoderado(null);
        setApoderadoExistente(false);

        // recargar la lista del modal para que se vea el nuevo apoderado/actualización
        await fetchInitialData();

        // limpiar campos del formulario (sin borrar la lista de apoderados del modal)
        setError(undefined);
        setTipoIdentificacion("");
        setIdentificacion("");
        setNombreCompleto("");
        setTarjetaProfesional("");
        setCelular("");
        setEmail("");
        setCiudad("");
        setDireccion("");

        // notificar al padre (p. ej. para recargar créditos/acreedores generales)
        if (typeof onSuccess === "function") onSuccess();
      } else {
        setError("No se pudo guardar el apoderado (respuesta inesperada).");
      }
    } catch (err) {
      console.error("Error al guardar los datos del Apoderado:", err);
      setError(obtenerMensajeError(err, "Error al guardar los datos del Apoderado."));
    }
  };

  // cerrar modal -> limpiar form pero no la lista que mostramos
  const hCClose = () => {
    resetFormFields();
    onClose();
  };

  // Eliminar asociación del apoderado seleccionado (actualiza el apoderadoToRemove)
  const removeApoderado = async (index) => {
    const apoderadoToRemove = apoderados[index];
    if (!apoderadoToRemove) return;

    try {
      // usar los responsables del propio apoderadoToRemove (no el state del form)
      const currentListAcreedores = Array.isArray(apoderadoToRemove.responsable?.list_acreedores)
        ? apoderadoToRemove.responsable.list_acreedores
        : [];
      const currentListClientes = Array.isArray(apoderadoToRemove.responsable?.list_clientes)
        ? apoderadoToRemove.responsable.list_clientes
        : [];

      const newListaResponsables = currentListAcreedores.filter((id) => id !== acreedor.id);

      // construir payload con la estructura esperada por la API
      const payload = {
        ...apoderadoToRemove,
        responsable: {
          list_clientes: currentListClientes,
          list_acreedores: newListaResponsables,
        },
      };

      await ApoderoadosApi.updateApoderado(apoderadoToRemove.id, payload);
      //const updated = resp?.data || resp;
      // si todo OK, actualizar la UI local
      setApoderados((prev) => prev.filter((_, i) => i !== index));
      // Si el apoderado removido era el que estaba cargado en el formulario, sincronizar listaResponsables
      if (apoderado && apoderado.id === apoderadoToRemove.id) {
        setListaResponsables(newListaResponsables);
      }
    } catch (err) {
      console.error("Error al eliminar el apoderado:", err);
      setError(obtenerMensajeError(err, "No se pudo eliminar el apoderado."));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <h2 style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
          {apoderado ? "Editar" : "Registrar"} Apoderado de
          <SuiTypography
            component="span"
            variant="h6"
            fontWeight="light"
            style={{ fontSize: "24px" }}
          >
            {acreedor ? ` ${acreedor.nombre_completo}` : " No tiene Acreedor"}
          </SuiTypography>
        </h2>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={12}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
            </Grid>
          </Grid>

          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={4}>
              <SuiTypography variant="h6" fontWeight="light">
                Identificación
              </SuiTypography>
              <SuiInput
                name="identificacion"
                placeholder="Identificación: dato numérico [0-9]"
                value={identificacion}
                onChange={handleIdentificacionChange}
                onBlur={handleIdentificacionChange}
                fullWidth
              />
            </Grid>

            <Grid size={4}>
              <SuiTypography variant="h6" fontWeight="light">
                Tipo de Identificación
              </SuiTypography>
              <Select
                value={tipoIdentificacion}
                onChange={(e) => setTipoIdentificacion(e.target.value)}
                displayEmpty
                fullWidth
              >
                <MenuItem value="CC">Cédula de Ciudadanía</MenuItem>
                <MenuItem value="NIT">Número de Identificación Tributaria</MenuItem>
                <MenuItem value="CE">Cédula de Extranjería</MenuItem>
                <MenuItem value="PAS">Pasaporte</MenuItem>
                <MenuItem value="OTRO">Otro</MenuItem>
              </Select>
            </Grid>

            <Grid size={4}>
              <SuiTypography variant="h6" fontWeight="light">
                Ciudad
              </SuiTypography>
              <SuiInput
                value={ciudad}
                onChange={(event) => {
                  setCiudad(event.target.value);
                  setError(undefined);
                }}
                fullWidth
              />
            </Grid>
          </Grid>

          <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={4}>
              <SuiTypography variant="h6" fontWeight="light">
                Nombre Completo
              </SuiTypography>
              <SuiInput
                value={nombreCompleto}
                onChange={(event) => {
                  setNombreCompleto(event.target.value);
                  setError(undefined);
                }}
                fullWidth
              />
            </Grid>

            <Grid size={4}>
              <SuiTypography variant="h6" fontWeight="light">
                No. Celular
              </SuiTypography>
              <SuiInput
                value={celular}
                onChange={(event) => {
                  setCelular(event.target.value);
                  setError(undefined);
                }}
                fullWidth
              />
            </Grid>

            <Grid size={4}>
              <SuiTypography variant="h6" fontWeight="light">
                Tarjeta Profesional
              </SuiTypography>
              <SuiInput
                value={tarjetaProfesional}
                onChange={(event) => {
                  setTarjetaProfesional(event.target.value);
                  setError(undefined);
                }}
              />
            </Grid>
          </Grid>

          <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={6}>
              <SuiTypography variant="h6" fontWeight="light">
                Correo Electrónico
              </SuiTypography>
              <SuiInput
                name="email"
                value={email}
                onChange={handleEmailChange}
                type="email"
                placeholder="Email"
              />
            </Grid>

            <Grid size={6}>
              <SuiTypography variant="h6" fontWeight="light">
                Dirección
              </SuiTypography>
              <SuiInput
                value={direccion}
                onChange={(event) => {
                  setDireccion(event.target.value);
                  setError(undefined);
                }}
                multiline
                rows={2}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <SuiButton onClick={hCClose} buttonColor="light">
            Cancelar
          </SuiButton>
          <SuiButton type="submit" variant="gradient" buttonColor="dark">
            {apoderadoExistente ? "Actualizar" : "Registrar"}
          </SuiButton>
        </DialogActions>

        {Array.isArray(apoderados) && apoderados.length > 0 && (
          <DialogContent>
            <SuiBox mb={2}>
              <SuiTypography variant="h4">Apoderados del Acreedor</SuiTypography>
            </SuiBox>

            <TableContainer component={Paper}>
              <Table>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                    Identificación
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                    Nombre Completo
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Celular</TableCell>
                  <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }} align="center">
                    Acciones
                  </TableCell>
                </TableRow>

                <TableBody>
                  {apoderados.map((apo, index) => (
                    <TableRow key={apo.id ?? index}>
                      <TableCell>
                        {apo.tipo_identificacion} - {apo.identificacion}
                      </TableCell>
                      <TableCell>{apo.nombre_completo}</TableCell>
                      <TableCell>{apo.email}</TableCell>
                      <TableCell>{apo.celular}</TableCell>
                      <TableCell align="center">
                        <IconButton onClick={() => removeApoderado(index)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
        )}
      </form>
    </Dialog>
  );
};

ApoderadoClienteModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  acreedor: PropTypes.object,
  onSuccess: PropTypes.func.isRequired,
};

export default ApoderadoClienteModal;
